import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable, Subject } from 'rxjs';
import { WheelComponent } from '../../../wheel/wheel.component';
import { ClientLinkData } from '../../../../services/client-link.resolver';
import { selectFocusedCategories, selectUserWheels } from '../../../../state/selectors';
import { loadWheelForParticipant, setActiveWheel } from 'src/app/state';
import { mapActiveWheel } from '../../../../utils/mappers/active-wheel.mapper';
import { filter } from 'rxjs/operators';
import { WheelStateCategory } from '../../../../state/typings';

@Component({
  selector: 'wil-client-view',
  standalone: true,
  imports: [WheelComponent],
  templateUrl: './client-view.component.html',
  styleUrl: './client-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();

  public clientData: ClientLinkData | null = null;
  public wheelId = '';
  public allCategories: CategorySnapshot[] = [];
  public hasDataError = false;
  public errorMessage = '';

  // Observables
  public focusedCategories$: Observable<WheelStateCategory[]> | undefined;

  ngOnInit(): void {
    // Get resolved data from route
    this.clientData = this.route.snapshot.data['clientData'];

    if (!this.clientData) {
      this.navigateToNotFound();
      return;
    }

    // Check for partial data errors
    if (this.hasPartialDataError()) {
      this.hasDataError = true;
      this.errorMessage = 'Some assessment data is missing. The wheel may not function properly.';
      // Continue rendering but with limited functionality
    }

    // Set wheel data for the wheel component or limited-mode rendering.
    this.wheelId = this.clientData.wheelId;
    this.allCategories = this.clientData.wheelVersion?.categories ?? [];

    this.store
      .select(selectUserWheels)
      .pipe(
        filter((w) => Boolean(w.length)),
        map((wheels) => mapActiveWheel(wheels, this.wheelId))
      )
      .subscribe((mappedWheel) => {
        if (!mappedWheel) return;

        this.store.dispatch(setActiveWheel({ wheel: mappedWheel }));
      });

    this.store.dispatch(loadWheelForParticipant({ wheelId: this.wheelId, accountId: this.clientData.accountId }));

    // Update observables with correct wheelId
    this.focusedCategories$ = this.store.select(selectFocusedCategories);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackByCategory(index: number, category: WheelStateCategory): string {
    return category.id;
  }

  public getHeaderGradient(): string {
    const primaryColor = this.clientData?.coach?.brand?.primaryColor;
    if (primaryColor) {
      // Create a gradient using the primary color with a slightly darker/lighter variant
      return `linear-gradient(135deg, ${primaryColor} 0%, ${this.adjustColorBrightness(primaryColor, 20)} 100%)`;
    }
    // Fallback to default gradient
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  }

  private adjustColorBrightness(color: string, percent: number): string {
    // Remove # if present
    const hex = color.replace('#', '');

    // Parse RGB values
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Adjust brightness
    const adjustedR = Math.max(0, Math.min(255, r + (percent * r) / 100));
    const adjustedG = Math.max(0, Math.min(255, g + (percent * g) / 100));
    const adjustedB = Math.max(0, Math.min(255, b + (percent * b) / 100));

    // Convert back to hex
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
  }

  private hasPartialDataError(): boolean {
    if (!this.clientData) return true;

    // Check if wheel version exists and has categories
    if (!this.clientData.wheelVersion || !this.clientData.wheelVersion.categories) {
      return true;
    }

    // Check if categories array is empty
    if (this.clientData.wheelVersion.categories.length === 0) {
      return true;
    }

    // Check if client data is incomplete
    if (!this.clientData.client.id || !this.clientData.client.name) {
      return true;
    }

    return false;
  }

  private navigateToNotFound(): void {
    this.router.navigate(['/client-link-not-found'], {
      skipLocationChange: false,
      replaceUrl: true,
    });
  }
}
